import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Shield, Zap, Sparkles } from 'lucide-react';

export default function CheckoutPage() {
  const [billingPeriod, setBillingPeriod] = useState('monthly'); // 'monthly' | 'annual'
  const navigate = useNavigate();

  const tiers = [
    {
      id: 'starter',
      name: 'Identity Starter',
      tagline: 'Establish your alignment baseline.',
      monthlyPrice: 4.99,
      annualPrice: 39.99,
      features: [
        'Up to 5 Beings',
        'Up to 10 Wants',
        'Personal Workspace Only',
        'Basic 7-day Analytics',
        'Standard Updates',
      ],
      icon: Shield,
      buttonText: 'Select Starter',
      popular: false,
    },
    {
      id: 'pro',
      name: 'Align Pro',
      tagline: 'Deepen your reflective practice.',
      monthlyPrice: 9.99,
      annualPrice: 79.99,
      features: [
        'Unlimited Beings & Wants',
        'Corporate & Family Workspaces',
        'Full AI Coach Access',
        'Advanced Analytics & Insights',
        'Priority feature updates',
      ],
      icon: Zap,
      buttonText: 'Select Pro',
      popular: true,
    },
    {
      id: 'elite',
      name: 'Elite Pioneer',
      tagline: 'Ultimate collaborative mastery.',
      monthlyPrice: 24.99,
      annualPrice: 199.99,
      features: [
        'All Pro Tier Benefits',
        'Up to 5 free workspace members',
        'Shared group vision boards',
        'Unlimited collaborative workspaces',
        'VIP support and custom features',
      ],
      icon: Sparkles,
      buttonText: 'Select Elite',
      popular: false,
    },
  ];

  const handleSelectPlan = (tierId) => {
    navigate(`/checkout/payment-method?tier=${tierId}&billing=${billingPeriod}`);
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8] dark:bg-[#121212] text-foreground py-16 px-4 sm:px-6 lg:px-8 transition-colors duration-200">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold tracking-tight text-primary sm:text-5xl mb-4 font-sans">
            Choose your path to alignment
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Select the subscription plan that best fits your reflective journey and start aligning your actions with your true self.
          </p>

          {/* Billing Toggle */}
          <div className="mt-8 flex justify-center items-center gap-3">
            <span className={`text-sm font-medium ${billingPeriod === 'monthly' ? 'text-primary' : 'text-muted-foreground'}`}>
              Monthly billing
            </span>
            <button
              onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'annual' : 'monthly')}
              className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out bg-primary focus:outline-none"
              role="switch"
              aria-checked={billingPeriod === 'annual'}
            >
              <span
                aria-hidden="true"
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow ring-0 transition duration-200 ease-in-out ${
                  billingPeriod === 'annual' ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
            <span className={`text-sm font-medium ${billingPeriod === 'annual' ? 'text-primary' : 'text-muted-foreground'}`}>
              Annual billing <span className="ml-1.5 inline-flex items-center rounded-full bg-emerald-100 dark:bg-emerald-950 px-2 py-0.5 text-xs font-semibold text-emerald-800 dark:text-emerald-300">Save ~20%</span>
            </span>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
          {tiers.map((tier) => {
            const Icon = tier.icon;
            const price = billingPeriod === 'monthly' ? tier.monthlyPrice : tier.annualPrice;
            const periodLabel = billingPeriod === 'monthly' ? '/mo' : '/yr';

            return (
              <div
                key={tier.id}
                className={`relative flex flex-col rounded-3xl p-8 bg-card border transition-all duration-300 ${
                  tier.popular
                    ? 'border-emerald-500 shadow-xl scale-105 z-10 dark:bg-zinc-900 bg-white ring-1 ring-emerald-500/50'
                    : 'border-border dark:bg-zinc-950 bg-neutral-50/50 shadow-md hover:border-muted-foreground/50'
                }`}
              >
                {tier.popular && (
                  <div className="absolute top-0 right-1/2 translate-x-1/2 -translate-y-1/2 rounded-full bg-emerald-500 px-4 py-1 text-xs font-bold text-white tracking-wide uppercase shadow">
                    Most Popular
                  </div>
                )}

                <div className="mb-6 flex items-center justify-between">
                  <div className="p-3 rounded-2xl bg-primary/10 text-primary">
                    <Icon className="h-6 w-6" />
                  </div>
                  {billingPeriod === 'annual' && (
                    <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/50 px-2.5 py-1 rounded-full">
                      ${((tier.monthlyPrice * 12 - tier.annualPrice)).toFixed(2)} saved/yr
                    </span>
                  )}
                </div>

                <h3 className="text-2xl font-bold text-foreground mb-2">{tier.name}</h3>
                <p className="text-sm text-muted-foreground min-h-[40px] mb-6">{tier.tagline}</p>

                <div className="flex items-baseline gap-1 mb-8">
                  <span className="text-4xl font-extrabold text-foreground">${price}</span>
                  <span className="text-sm font-medium text-muted-foreground">{periodLabel}</span>
                </div>

                <ul className="space-y-4 mb-8 flex-1">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-foreground/90">
                      <Check className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSelectPlan(tier.id)}
                  className={`w-full py-3 px-4 rounded-xl font-semibold transition-all duration-200 focus:outline-none ${
                    tier.popular
                      ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-500/20 active:scale-[0.98]'
                      : 'bg-primary hover:bg-primary/95 text-primary-foreground shadow active:scale-[0.98]'
                  }`}
                >
                  {tier.buttonText}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
